import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface SavedCard {
  id: string;
  type: 'mastercard' | 'amex' | 'visa';
  lastFour: string;
  cardholderName: string;
  expiryDate: string;
  isDefault: boolean;
  logoSrc: string;
  paymentMethodType?: string;
  bankName?: string;
  bankAccountType?: string;
  creditCardType?: string;
  creditCardNetworkType?: string;
  billingZipCode?: string;
  /** Street address OneInc has on file for the card, used by the edit form. */
  cardHolderAddress?: string;
  /** Raw OneInc expiration parts, kept so the edit form can prefill exactly. */
  cardExpirationMonth?: number;
  cardExpirationYear?: number;
}

export interface PaymentMethodState {
  savedCards: SavedCard[];
  customerId: string;
  processingFeeEligible: boolean;
  /** Fee charged on card payments, as returned by getProcessingFee. */
  convenienceFee: string;
  loaded: boolean;
  /** Processing fee percentage, e.g., "2.5" for 2.5%. */
  currentProcessingFeePercent: string;
}

const initialState: PaymentMethodState = {
  savedCards: [],
  customerId: '',
  processingFeeEligible: false,
  convenienceFee: '',
  loaded: false,
  currentProcessingFeePercent: '',
};

@Injectable({ providedIn: 'root' })
export class PaymentMethodStateService {
  private readonly state$ = new BehaviorSubject<PaymentMethodState>(initialState);

  readonly snapshot$ = this.state$.asObservable();

  get snapshot(): PaymentMethodState {
    return this.state$.getValue();
  }

  get isLoaded(): boolean {
    return this.state$.getValue().loaded;
  }

  patch(partial: Partial<Omit<PaymentMethodState, 'loaded'>>): void {
    this.state$.next({ ...this.state$.getValue(), ...partial });
  }

  markLoaded(): void {
    this.state$.next({ ...this.state$.getValue(), loaded: true });
  }

  invalidate(): void {
    this.state$.next(initialState);
  }
}
